/**
 * WHEN / IF / DO plain-language projection of an HA automation.
 *
 * IMPORTANT — conceptual boundary: HA automations, NOT TerraMatrix native
 * automation rules.
 *
 * SAFETY (roadmap Scope Decision 5 / Audit): this projection MUST NOT
 * oversimplify. Any construct that does not map cleanly to a faithful
 * sentence — `choose`/`repeat`/`parallel`/`if`/`wait_template`/
 * `wait_for_trigger`, or ANY value containing a `{{ }}` Jinja template — is
 * emitted as `kind:'complex'` with NO summary so the UI shows the raw block
 * flagged "review carefully". A confident-but-wrong summary of an automation
 * controlling locks/alarms is dangerous; prefer `complex` when in doubt.
 *
 * This is a PROJECTION of the config (display only). The config stays the
 * source of truth — never reconstruct an automation from this model.
 */

import type {
  HAAutomationConfig,
  HATriggerConfig,
  HAConditionConfig,
  HAActionConfig,
  ReviewBlock,
  WhenIfDoModel,
} from '@/types/haAutomation';

/** Optional entity-id → friendly-name resolver (defaults to identity). */
export type EntityNameResolver = (entityId: string) => string;

const identity: EntityNameResolver = (id) => id;

/** True if any nested string value contains a Jinja template. */
function containsTemplate(value: unknown): boolean {
  if (typeof value === 'string') return value.includes('{{') || value.includes('{%');
  if (Array.isArray(value)) return value.some(containsTemplate);
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(containsTemplate);
  }
  return false;
}

function asList(v: string | string[] | undefined): string[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

function names(v: string | string[] | undefined, resolve: EntityNameResolver): string {
  const list = asList(v).map(resolve);
  return list.length > 0 ? list.join(', ') : 'an entity';
}

const complex = (raw: unknown): ReviewBlock => ({ kind: 'complex', raw });
const plain = (summary: string, raw: unknown): ReviewBlock => ({ kind: 'plain', summary, raw });

function projectTrigger(t: HATriggerConfig, resolve: EntityNameResolver): ReviewBlock {
  if (containsTemplate(t)) return complex(t);
  switch (t.platform) {
    case 'state':
      return plain(
        `${names(t.entity_id, resolve)} changes${t.to ? ` to "${String(t.to)}"` : ''}${
          t.from ? ` from "${String(t.from)}"` : ''
        }`,
        t,
      );
    case 'numeric_state':
      return plain(
        `${names(t.entity_id, resolve)} goes ${
          t.above !== undefined ? `above ${String(t.above)}` : ''
        }${t.above !== undefined && t.below !== undefined ? ' / ' : ''}${
          t.below !== undefined ? `below ${String(t.below)}` : ''
        }`,
        t,
      );
    case 'time':
      return plain(`the time is ${asList(t.at).join(', ') || 'a set time'}`, t);
    case 'sun':
      return plain(
        `${t.event === 'sunset' ? 'sunset' : 'sunrise'}${t.offset ? ` (offset ${t.offset})` : ''}`,
        t,
      );
    case 'time_pattern':
      return plain('a recurring time pattern', t);
    case 'zone':
      return plain(`${names(t.entity_id, resolve)} enters/leaves zone ${String(t.zone ?? '')}`, t);
    case 'mqtt':
      return plain(`an MQTT message on "${String(t.topic ?? '')}"`, t);
    case 'webhook':
      return plain('a webhook is called', t);
    case 'device':
      return plain('a device trigger fires', t);
    default:
      // Unknown/extensible platform — do not guess.
      return complex(t);
  }
}

function projectCondition(c: HAConditionConfig, resolve: EntityNameResolver): ReviewBlock {
  if (containsTemplate(c)) return complex(c);
  switch (c.condition) {
    case 'state':
      return plain(
        `${names(c.entity_id, resolve)} is "${String(c.state ?? '')}"`,
        c,
      );
    case 'numeric_state':
      return plain(
        `${names(c.entity_id, resolve)} is ${
          c.above !== undefined ? `above ${String(c.above)}` : ''
        }${c.above !== undefined && c.below !== undefined ? ' / ' : ''}${
          c.below !== undefined ? `below ${String(c.below)}` : ''
        }`,
        c,
      );
    case 'time':
      return plain('the time is within a set window', c);
    case 'and':
    case 'or':
    case 'not':
      // Nested boolean logic — do not flatten into a misleading sentence.
      return complex(c);
    default:
      return complex(c);
  }
}

function projectAction(a: HAActionConfig, resolve: EntityNameResolver): ReviewBlock {
  // Control-flow / template constructs are never summarized (safety).
  if (
    a.choose !== undefined ||
    a.repeat !== undefined ||
    a.parallel !== undefined ||
    a.if !== undefined ||
    a.wait_template !== undefined ||
    'wait_for_trigger' in a ||
    containsTemplate(a)
  ) {
    return complex(a);
  }
  if (typeof a.service === 'string') {
    const target =
      a.target?.entity_id !== undefined
        ? names(a.target.entity_id, resolve)
        : a.target?.area_id !== undefined
          ? `area ${asList(a.target.area_id).join(', ')}`
          : 'the configured target';
    return plain(`call ${a.service} on ${target}`, a);
  }
  if (typeof a.scene === 'string') return plain(`activate scene ${a.scene}`, a);
  if (a.delay !== undefined) return plain(`wait ${JSON.stringify(a.delay)}`, a);
  if (typeof a.event === 'string') return plain(`fire event ${a.event}`, a);
  return complex(a);
}

export function toWhenIfDo(
  config: HAAutomationConfig,
  resolveEntityName: EntityNameResolver = identity,
): WhenIfDoModel {
  return {
    when: (config.trigger ?? []).map((t) => projectTrigger(t, resolveEntityName)),
    if: (config.condition ?? []).map((c) => projectCondition(c, resolveEntityName)),
    do: (config.action ?? []).map((a) => projectAction(a, resolveEntityName)),
  };
}
